import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, Share2, Lock, Mail, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import SignupModal from "@/components/SignupModal";
import SignInOverlay from "@/components/SignInOverlay";


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Function to clean AI-generated content by removing citation brackets like [1], {4}, etc.
const cleanContent = (content) => {
  if (!content) return content;
  // Remove [number], {number}, and similar patterns
  return content
    .replace(/\[\d+\]/g, '')
    .replace(/\{\d+\}/g, '')
    .replace(/\(\d+\)/g, '')
    .replace(/【\d+】/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Custom markdown components for styling (defined outside component to prevent re-renders)
const markdownComponents = {
  h2: (props) => <h2 className="text-2xl font-heading font-bold text-navy-700 mt-8 mb-4" {...props} />,
  h3: (props) => <h3 className="text-xl font-heading font-semibold text-navy-700 mt-6 mb-3" {...props} />,
  p: (props) => <p className="text-slate-600 leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc list-inside space-y-2 mb-4 text-slate-600" {...props} />,
  ol: (props) => <ol className="list-decimal list-inside space-y-2 mb-4 text-slate-600" {...props} />,
  li: (props) => <li className="text-slate-600" {...props} />,
  strong: (props) => <strong className="font-semibold text-navy-700" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-500 my-4" {...props} />,
  a: (props) => <a className="text-emerald-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupOpen, setSignupOpen] = useState(false);
  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateLoading, setGateLoading] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // Check if user already has access (signed in or subscribed)
    const accessGranted = localStorage.getItem("blog_access_granted");
    const userToken = localStorage.getItem("user_token");
    const hasSubscribed = localStorage.getItem("compoundr_subscribed");
    if (accessGranted || userToken || hasSubscribed) {
      setHasAccess(true);
    }
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Split content into preview and gated sections (for markdown)
  const splitContent = (content) => {
    if (!content) return { preview: "", gated: "", isHtml: false };
    
    // Check if content is HTML or Markdown
    const isHtml = content.startsWith("<") || content.includes("</p>");
    
    if (isHtml) {
      // Split HTML by paragraphs - show only first 2 paragraphs
      const paragraphs = content.split(/<\/p>/);
      const previewParagraphs = paragraphs.slice(0, 2).join("</p>") + "</p>";
      const gatedParagraphs = paragraphs.slice(2).join("</p>");
      return { preview: previewParagraphs, gated: gatedParagraphs, isHtml: true };
    } else {
      // Split Markdown by double newlines (paragraphs)
      const paragraphs = content.split(/\n\n+/);
      const previewParagraphs = paragraphs.slice(0, 3).join("\n\n");
      const gatedParagraphs = paragraphs.slice(3).join("\n\n");
      return { preview: previewParagraphs, gated: gatedParagraphs, isHtml: false };
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!gateEmail) {
      toast.error("Please enter your email");
      return;
    }

    setGateLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscribers`, {
        email: gateEmail,
        phone: "",
        sms_consent: false,
        source: "blog_gate",
      });
      localStorage.setItem("blog_access_granted", "true");
      setHasAccess(true);
      setEmailGateOpen(false);
      toast.success("Welcome! Enjoy the full article.");
    } catch (error) {
      if (error.response?.data?.detail === "Email already subscribed") {
        localStorage.setItem("blog_access_granted", "true");
        setHasAccess(true);
        setEmailGateOpen(false);
        toast.success("Welcome back! Enjoy the full article.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setGateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="h-12 bg-slate-200 rounded" />
            <div className="h-64 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-heading font-bold text-navy-700 mb-4">
            Article Not Found
          </h1>
          <Link to="/blog">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const contentData = splitContent(post.content);
  const { preview, gated } = contentData;

  return (
    <SignInOverlay message="Sign in to read this article">
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Header */}
      <section className="py-16 lg:py-24 gradient-mesh">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center text-slate-600 hover:text-navy-700 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              {post.category}
            </Badge>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-navy-700 mb-6">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.image_url && (
        <div className="max-w-4xl mx-auto px-4 -mt-8">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-[400px] object-cover rounded-2xl shadow-floating"
          />
        </div>
      )}

      {/* Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Preview Content - Always visible */}
          <motion.article
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="blog-content"
          >
            {contentData.isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: cleanContent(preview) }} />
            ) : (
              <ReactMarkdown components={markdownComponents}>{cleanContent(preview)}</ReactMarkdown>
            )}
          </motion.article>

          {/* Email Gate */}
          {!hasAccess && gated && (
            <div className="relative mt-8">
              {/* Blurred preview of gated content */}
              <div 
                className="blog-content blur-sm select-none pointer-events-none"
                style={{ maxHeight: "200px", overflow: "hidden" }}
              >
                {contentData.isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: cleanContent(gated.substring(0, 500)) }} />
                ) : (
                  <ReactMarkdown components={markdownComponents}>{cleanContent(gated.substring(0, 500))}</ReactMarkdown>
                )}
              </div>
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white" />
              
              {/* Email capture card */}
              <Card className="absolute bottom-0 left-0 right-0 border-2 border-emerald-200 shadow-floating bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
                    Enter your email to continue reading
                  </h3>
                  <p className="text-slate-500 text-sm mb-6">
                    (We promise not to bombard you with alerts)
                  </p>
                  <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={gateEmail}
                      onChange={(e) => setGateEmail(e.target.value)}
                      className="flex-1"
                      data-testid="blog-gate-email"
                    />
                    <Button
                      type="submit"
                      disabled={gateLoading}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="blog-gate-submit"
                    >
                      {gateLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Continue Reading
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Full Content - After email */}
          {hasAccess && gated && (
            <motion.article
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="blog-content"
            >
              {contentData.isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: cleanContent(gated) }} />
              ) : (
                <ReactMarkdown components={markdownComponents}>{cleanContent(gated)}</ReactMarkdown>
              )}
            </motion.article>
          )}

          {/* Share */}
          <div className="border-t border-b py-6 mt-12 flex items-center justify-between">
            <span className="text-slate-600">Found this helpful?</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard.writeText(url);
                toast.success("Link copied!");
              }}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Article
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-navy-700 to-navy-600 border-0">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-heading font-bold text-white mb-3">
                Want More Like This?
              </h3>
              <p className="text-slate-300 mb-6">
                Get weekly investing tips and exclusive content delivered to your
                inbox.
              </p>
              <Button
                onClick={() => setSignupOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8"
                data-testid="blogpost-signup-btn"
              >
                Join Free Newsletter
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <SignupModal open={signupOpen} onOpenChange={setSignupOpen} source="blog" />
      
    </div>
    </SignInOverlay>
  );
};

export default BlogPost;
